// Per-service calculator configuration.
// Each service in /pricing/:slug has its own dedicated calculator.
// Pricing logic stays identical to the global calculator: area × rate,
// floored at minPrice and rounded to the nearest ₹100.

export const CALC_CONFIGS = {
  // ---------- TERMITE SOLUTIONS ----------
  'anti-termite-treatment': {
    icon: '🛡️',
    pestLabel: 'Anti Termite Treatment',
    pestKey: 'termite',
    rate: 6,
    minPrice: 2499,
    pricingModel: 'tier',           // tier-based final price (rounded to ₹100)
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Termite Treatment',
    note: 'Final price may vary after on-site inspection.',
  },
  'pre-construction-anti-termite': {
    icon: '🏗️',
    pestLabel: 'Pre-Construction Anti-Termite',
    pestKey: 'termite',
    rate: 8,
    minPrice: 0,
    pricingModel: 'per-sqft',       // displays ₹ rate × area, no floor
    showBhk: false,
    showCustomArea: true,
    customAreaLabel: 'Plinth / Foundation Area (sq ft)',
    customAreaPlaceholder: 'e.g., 1500',
    cta: 'Get PCT Quote',
    note: 'Rate applies to plinth + foundation area as per IS 6313.',
  },
  'post-construction-anti-termite': {
    icon: '💉',
    pestLabel: 'Post-Construction Anti-Termite',
    pestKey: 'termite',
    rate: 7,
    minPrice: 2999,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Inspection & Treatment',
    note: 'Drill-and-inject treatment with sealed finish.',
  },
  'reticulation-system': {
    icon: '⚙️',
    pestLabel: 'Reticulation System',
    pestKey: 'termite',
    rate: 15,
    minPrice: 0,
    pricingModel: 'per-sqft',
    showBhk: false,
    showCustomArea: true,
    customAreaLabel: 'Built-up Area (sq ft)',
    customAreaPlaceholder: 'e.g., 2500',
    cta: 'Request Site Survey',
    note: 'Includes pipe layout, valves and first charge.',
  },

  // ---------- GENERAL PEST CONTROL ----------
  'cockroach-control': {
    icon: '🪳',
    pestLabel: 'Cockroach Control',
    pestKey: 'cockroach',
    rate: 3.5,
    minPrice: 799,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Cockroach Treatment',
    note: 'Includes gel baiting + residual spray.',
  },
  'rodent-control': {
    icon: '🐀',
    pestLabel: 'Rodent Control',
    pestKey: 'rodent',
    rate: 3,
    minPrice: 999,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Rodent Removal',
    note: 'Traps, bait stations & entry-point sealing.',
  },
  'ant-control': {
    icon: '🐜',
    pestLabel: 'Ant Control',
    pestKey: 'ant',
    rate: 3,
    minPrice: 699,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Ant Treatment',
    note: 'Colony-level gel baiting + perimeter spray.',
  },
  'mosquito-fogging': {
    icon: '⚡',
    pestLabel: 'Mosquito Fogging',
    pestKey: 'mosquito',
    rate: 4,
    minPrice: 899,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Mosquito Fogging',
    note: 'Indoor ULV + outdoor thermal fogging.',
  },
  'bed-bug-removal': {
    icon: '🛏️',
    pestLabel: 'Bed Bug Removal',
    pestKey: 'bedbug',
    rate: 5,
    minPrice: 1499,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Bed Bug Treatment',
    note: 'Steam + chemical treatment with mandatory follow-up.',
  },
  'spider-control': {
    icon: '🕷️',
    pestLabel: 'Spider Control',
    pestKey: 'spider',
    rate: 3,
    minPrice: 799,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Spider Treatment',
    note: 'Web removal + residual spray for 60+ days protection.',
  },
  'lizard-control': {
    icon: '🦎',
    pestLabel: 'Lizard Control',
    pestKey: 'lizard',
    rate: 3.5,
    minPrice: 899,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Lizard Treatment',
    note: 'Odourless, food-safe repellent. Humane — does not kill.',
  },
  'fly-control': {
    icon: '🪰',
    pestLabel: 'Fly Control',
    pestKey: 'fly',
    rate: 3,
    minPrice: 799,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Fly Treatment',
    note: 'Drain gel + residual spray. FSSAI-safe for restaurants.',
  },
  'silverfish-control': {
    icon: '🐛',
    pestLabel: 'Silverfish Control',
    pestKey: 'silverfish',
    rate: 3,
    minPrice: 799,
    pricingModel: 'tier',
    showBhk: true,
    showCustomArea: true,
    cta: 'Book Silverfish Treatment',
    note: 'Spray + dust treatment. Safe for books, fabrics & pantries.',
  },

  // ---------- SPECIALIZED ----------
  'wood-borer': {
    icon: '🪵',
    pestLabel: 'Wood Borer Treatment',
    pestKey: 'general',
    rate: 4,
    minPrice: 1299,
    pricingModel: 'tier',
    showBhk: false,
    showCustomArea: true,
    customAreaLabel: 'Affected Wood Area (sq ft)',
    customAreaPlaceholder: 'e.g., 200',
    cta: 'Book Wood Borer Treatment',
    note: 'Boron injection + permethrin surface spray.',
  },
  'honeybee-removal': {
    icon: '🐝',
    pestLabel: 'Honeybee Removal',
    pestKey: 'general',
    rate: 0,
    minPrice: 1499,
    pricingModel: 'flat-by-hive',  // pick by hive size
    showBhk: false,
    showCustomArea: false,
    hiveOptions: [
      { value: 'small', label: 'Small Hive', desc: 'Up to 1 ft', price: 1499 },
      { value: 'medium', label: 'Medium Hive', desc: '1-2 ft', price: 2799 },
      { value: 'large', label: 'Large Hive', desc: '2+ ft / multiple', price: 5499 },
    ],
    cta: 'Book Live Bee Removal',
    note: 'Live, humane relocation by certified handlers.',
  },
};

export const getCalcConfig = (slug) => CALC_CONFIGS[slug] || null;

export const PRICING_MENU = [
  {
    label: 'Termite Solutions',
    slugs: [
      'anti-termite-treatment',
      'pre-construction-anti-termite',
      'post-construction-anti-termite',
      'reticulation-system',
    ],
  },
  {
    label: 'General Pest Control',
    slugs: ['cockroach-control', 'rodent-control', 'ant-control', 'mosquito-fogging'],
  },
  {
    label: 'Specialized Services',
    slugs: ['wood-borer', 'honeybee-removal'],
  },
];
